import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getAuth } from 'firebase/auth';
import dayjs from 'dayjs';
import { API_URL } from '../../constants/string-constants';
import DMMessageBubble from './dm/DMMessageBubble';
import ModBanModal from './ModBanModal';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3000;
  padding: 2rem;
`;

const ModalContent = styled.div`
  background-color: var(--clr-prim);
  border-radius: 12px;
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  color: var(--clr-sec);
  margin: 0;
  font-size: 1.3rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const UserIdBadge = styled.span`
  background-color: var(--clr-purple);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-family: monospace;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--clr-sec);
  padding: 0.5rem;
  border-radius: 6px;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem 2rem;
`;

const ReportItem = styled.div`
  background-color: var(--clr-bg);
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.05);
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ReportHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const ReportMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ReportTime = styled.div`
  color: var(--clr-sec);
  font-size: 0.9rem;
  opacity: 0.8;
`;

const ReportReason = styled.div`
  color: var(--clr-sec);
  font-size: 0.9rem;
  
  &.serious-violation {
    color: #ff6b35;
    font-weight: 600;
  }
`;

const ReportActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.2s;
`;

const ResolveButton = styled(ActionButton)`
  background-color: var(--clr-green);
  color: white;
  
  &:hover {
    background-color: #28a745;
  }
`;

const BanButton = styled(ActionButton)`
  background-color: #ff4444;
  color: white;
  
  &:hover {
    background-color: #dd3333;
  }
`;

const ReportedMessageSection = styled.div`
  margin-bottom: 1rem;
`;

const ReportedMessageHeader = styled.div`
  color: var(--clr-sec);
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ReportedMessageContainer = styled.div`
  background-color: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1rem;
  border-radius: 6px;
  border-left: 3px solid #ff6b35;
  
  .dm-message-container {
    margin: 0;
  }
`;

const AdditionalContext = styled.div`
  background-color: rgba(255, 255, 255, 0.02);
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  border-left: 3px solid var(--clr-purple);
`;

const ContextLabel = styled.div`
  color: var(--clr-purple);
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
`;

const ContextText = styled.div`
  color: var(--clr-sec);
  line-height: 1.4;
`;

const MessageContext = styled.div`
  margin-top: 1rem;
`;

const ContextHeader = styled.div`
  color: var(--clr-sec);
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ContextToggle = styled.button`
  background: none;
  border: none;
  color: var(--clr-purple);
  cursor: pointer;
  font-size: 0.8rem;
  text-decoration: underline;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ContextMessages = styled.div`
  max-height: 300px;
  overflow-y: auto;
  background-color: rgba(255, 255, 255, 0.01);
  border-radius: 6px;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

const ContextMessage = styled.div`
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  
  &:last-child {
    border-bottom: none;
  }
  
  ${props => props.$isReported && `
    background-color: rgba(255, 255, 255, 0.03);
    border-radius: 4px;
    padding: 0.75rem;
    margin: 0.25rem 0;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-left: 3px solid #ff6b35;
  `}
  
  ${props => props.$isFromReportedUser && !props.$isReported && `
    background-color: rgba(255, 255, 255, 0.02);
    border-radius: 4px;
    padding: 0.75rem;
    margin: 0.25rem 0;
    border: 1px solid rgba(255, 255, 255, 0.05);
    opacity: 0.8;
  `}
`;

const ContextMessageText = styled.div`
  color: ${props => props.$color || 'var(--clr-sec)'};
  font-size: 0.9rem;
  font-family: ${props => {
    if (props.$font === 'Caveat') {
      return "'Caveat', cursive";
    } else if (props.$font === 'JetBrains Mono') {
      return "'JetBrains Mono', 'Courier New', monospace";
    } else if (props.$font === 'Inter') {
      return "'Inter', system-ui, -apple-system, sans-serif";
    } else if (props.$font === 'Roboto') {
      return "'Roboto', system-ui, -apple-system, sans-serif";
    } else {
      return 'inherit';
    }
  }};
  ${props => props.$effect === 'glow' && `
    text-shadow: 
      0 0 3px ${props.$color || 'var(--clr-sec)'}, 
      0 0 6px ${props.$color || 'var(--clr-sec)'};
  `}
`;

const ContextMessageMeta = styled.div`
  color: ${props => props.$color || 'var(--clr-sec)'};
  opacity: 0.6;
  font-size: 0.75rem;
  display: flex;
  gap: 0.5rem;
  align-items: center;
  
  & .nickname {
    font-style: italic;
    opacity: 0.8;
  }
  
  & .reported-label {
    color: var(--clr-purple);
    font-weight: 600;
    opacity: 1;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--clr-sec);
  opacity: 0.7;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #ff4444;
`;

const REASON_LABELS = {
  'spam': 'Spam',
  'harassment': 'Harassment or Bullying',
  'inappropriate_content': 'Inappropriate Content',
  'hate_speech': 'Hate Speech',
  'threatening_behavior': 'Threatening Behavior',
  'personal_information': 'Sharing Personal Information',
  'impersonation': 'Impersonation',
  'other': 'Other'
};

const SERIOUS_VIOLATIONS = ['hate_speech', 'threatening_behavior', 'harassment'];

const ReportDetailsModal = ({ isOpen, onClose, userId, room }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedContext, setExpandedContext] = useState({});
  const [userNickname, setUserNickname] = useState(null);
  const [banModalOpen, setBanModalOpen] = useState(false);
  const [reportToBan, setReportToBan] = useState(null);

  const fetchUserReports = async () => {
    if (!userId) return;
    
    setLoading(true);
    setError('');
    
    try {
      const token = await getAuth().currentUser.getIdToken();
      
      // Use global endpoint for global mods, room-specific endpoint otherwise
      const endpoint = room === 'global' ? 
        `${API_URL}report/mod/global/${userId}` : 
        `${API_URL}report/mod/${room}/${userId}`;
      
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setReports(data.reports || []);
        
        // Extract nickname from the first report's message snapshots
        if (data.reports && data.reports.length > 0 && data.reports[0].messageSnapshots) {
          const snapshot = data.reports[0].messageSnapshots.find(s => s.userId === userId);
          if (snapshot) {
            setUserNickname(snapshot.nickname);
          }
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch user reports');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && userId) {
      fetchUserReports();
    }
  }, [isOpen, userId, room]);

  const handleResolveReport = async (reportId) => {
    try {
      const token = await getAuth().currentUser.getIdToken();
      
      const response = await fetch(`${API_URL}report/${reportId}/resolve`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'manual_resolve'
        }),
      });

      if (response.ok) {
        // Remove the resolved report from the list
        setReports(prev => prev.filter(report => report.id !== reportId));
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to resolve report');
      }
    } catch (err) {
      alert('Network error. Please try again.');
    }
  };

  const toggleContext = (reportId) => {
    setExpandedContext(prev => ({
      ...prev,
      [reportId]: !prev[reportId]
    }));
  };

  const handleBanUser = (report) => {
    // Debug: Log report structure to see available fields
    console.log('Report object:', report);
    
    // Determine the actual room for the ban
    // If report has a room field, use that; otherwise use the current room unless it's 'global'
    const actualRoom = report.room || report.roomId || (room !== 'global' ? room : 'en-chat');
    console.log('Using room for ban:', actualRoom);
    
    // Create a message object for the ban modal from the report
    setReportToBan({
      userId: userId,
      messageText: report.messageText,
      nickname: userNickname || 'Anonymous',
      messageId: report.messageId,
      actualRoom: actualRoom // Pass the actual room to use for the ban
    });
    setBanModalOpen(true);
  };

  const handleBanComplete = () => {
    setBanModalOpen(false);
    setReportToBan(null);
    // Refresh reports after successful ban
    fetchUserReports();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            Report Details
            <UserIdBadge>
              {userNickname || 'Anonymous'} ({userId.substring(0, 8)}...)
            </UserIdBadge>
          </ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        <ModalBody>
          {loading ? (
            <LoadingMessage>Loading report details...</LoadingMessage>
          ) : error ? (
            <ErrorMessage>{error}</ErrorMessage>
          ) : reports.length === 0 ? (
            <LoadingMessage>No reports found for this user.</LoadingMessage>
          ) : (
            reports.map((report) => (
              <ReportItem key={report.id}>
                <ReportedMessageSection>
                  <ReportedMessageHeader>
                    📢 Reported Message
                  </ReportedMessageHeader>
                  <ReportedMessageContainer>
                    <DMMessageBubble
                      message={{
                        messageText: report.messageText,
                        color: report.messageColor || 'var(--clr-sec)',
                        font: report.messageFont,
                        effect: report.messageEffect,
                        bold: report.messageBold,
                        createdAt: report.originalTimestamp || report.createdAt,
                        sender: {
                          nickname: userNickname || 'Anonymous'
                        }
                      }}
                      isFromMe={false}
                      friendColor={report.messageColor || 'var(--clr-sec)'}
                      friendNickname={userNickname || 'Anonymous'}
                      showTimestamp={true}
                    />
                  </ReportedMessageContainer>
                </ReportedMessageSection>

                <ReportHeader>
                  <ReportMeta>
                    <ReportTime>
                      {dayjs(report.createdAt).format('MMM D, YYYY [at] h:mm A')}
                    </ReportTime>
                    <ReportReason className={SERIOUS_VIOLATIONS.includes(report.reason) ? 'serious-violation' : ''}>
                      Reason: {REASON_LABELS[report.reason] || report.reason}
                    </ReportReason>
                    <ReportReason>
                      Channel: {report.room || report.language || room}
                    </ReportReason>
                    {report.reporterId && (
                      <ReportReason>
                        Reported by: {report.reporterNickname || `${report.reporterId.substring(0, 8)}...`}
                      </ReportReason>
                    )}
                  </ReportMeta>
                  <ReportActions>
                    <ResolveButton onClick={() => handleResolveReport(report.id)}>
                      Resolve
                    </ResolveButton>
                    <BanButton onClick={() => handleBanUser(report)}>
                      Ban User
                    </BanButton>
                  </ReportActions>
                </ReportHeader>

                {report.additionalContext && (
                  <AdditionalContext>
                    <ContextLabel>Additional Context</ContextLabel>
                    <ContextText>{report.additionalContext}</ContextText>
                  </AdditionalContext>
                )}

                <MessageContext>
                  <ContextHeader>
                    Message Context ({report.messageSnapshots?.length || 0} messages)
                    <ContextToggle onClick={() => toggleContext(report.id)}>
                      {expandedContext[report.id] ? 'Hide' : 'Show'} Context
                    </ContextToggle>
                  </ContextHeader>

                  {expandedContext[report.id] && (
                    <ContextMessages>
                      {report.messageSnapshots?.map((snapshot, index) => (
                        <ContextMessage 
                          key={snapshot.id}
                          $isReported={snapshot.messageId === report.messageId}
                          $isFromReportedUser={snapshot.userId === userId}
                        >
                          <ContextMessageText
                            $color={snapshot.color}
                            $font={snapshot.font}
                            $effect={snapshot.effect}
                          >
                            {snapshot.messageText}
                          </ContextMessageText>
                          <ContextMessageMeta $color={snapshot.color}>
                            {snapshot.nickname && (
                              <>
                                <span className="nickname">
                                  {snapshot.nickname}
                                </span>
                                <span>•</span>
                              </>
                            )}
                            <span>
                              {dayjs(snapshot.originalTimestamp).format('HH:mm')}
                            </span>
                            {snapshot.messageId === report.messageId && (
                              <>
                                <span>•</span>
                                <span className="reported-label">
                                  REPORTED MESSAGE
                                </span>
                              </>
                            )}
                          </ContextMessageMeta>
                        </ContextMessage>
                      ))}
                    </ContextMessages>
                  )}
                </MessageContext>
              </ReportItem>
            ))
          )}
        </ModalBody>
      </ModalContent>
      
      <ModBanModal
        isOpen={banModalOpen}
        onClose={() => setBanModalOpen(false)}
        message={reportToBan}
        room={reportToBan?.actualRoom || room}
        onBanComplete={handleBanComplete}
      />
    </ModalOverlay>
  );
};

export default ReportDetailsModal;